import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, TrendingUp, Activity, Wallet, AlertTriangle, ShoppingCart, ArrowRightLeft, Clock3, Loader2 } from "lucide-react";
import { Tile, Badge } from "../Primitives";
import { OriginBadge, recOrigin, combineOrigins } from "../../lib/origin.jsx";
import { morningBrief, ENGINES } from "../../lib/enginesApi";
import { useRole } from "../../lib/role.jsx";
import { useCopilot } from "../../lib/copilot.jsx";
import { formatPeso, cn } from "../../lib/utils";

const ICON = {
  demand_forecast: TrendingUp, stockout_adjusted: Activity, inventory_health: Wallet,
  stockout_risk: AlertTriangle, replenishment: ShoppingCart, redistribution: ArrowRightLeft, aging: Clock3,
};
const TONE = { stockout_risk: "critical", inventory_health: "warning", aging: "warning", replenishment: "info", redistribution: "success" };
const ROUTE = { replenishment: "/procurement", demand_forecast: "/forecast", stockout_adjusted: "/forecast" };
const LABEL = Object.fromEntries(ENGINES.map(e => [e.id, e.label]));

export default function DecisionsTile({ branchId }) {
  const navigate = useNavigate();
  const { canAccess } = useRole();
  const { askCopilot } = useCopilot();
  const [recs, setRecs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    morningBrief(branchId).then(b => setRecs((b.top5 || []).map(r => ({ ...r, engine_label: r.engine_label || LABEL[r.engine] }))))
      .catch(e => setError(e.message));
  }, [branchId]);

  const total = (recs || []).reduce((s, r) => s + (r.expected_value_php || 0), 0);
  const origin = recs?.length ? { ...recOrigin(recs[0]), origin: combineOrigins(recs.map(r => recOrigin(r).origin)) } : null;

  return (
    <Tile className="col-span-12" testid="top-decisions" title="Today's Top 5 Decisions · Inventory Engines" origin={origin}
          action={recs?.length > 0 && <div className="flex items-center gap-2">
            <Badge status="info">Ranked by ₱ impact</Badge>
            <button data-testid="ask-copilot-top-decisions"
                    onClick={() => askCopilot(`Give me a briefing on today's top 5 inventory engine decisions: ${recs.map((r, i) => `(${i + 1}) [${r.engine_label}] ${r.title} — ${formatPeso(r.expected_value_php || 0, { compact: true })}`).join("; ")}. Total ${formatPeso(total, { compact: true })}. Sequence them, name owners, and flag second-order stock or demand effects.`)}
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium">
              <MessageSquare size={11} /> Brief me →
            </button>
          </div>}>
      {error && <div data-testid="top-decisions-error" className="text-[12px] text-critical">{error}</div>}
      {!recs && !error && <div className="text-muted text-[12px] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading engine decisions…</div>}
      {recs?.length === 0 && <div data-testid="top-decisions-empty" className="text-muted text-[12px]">No pending engine actions. Run a cycle from Morning Brief.</div>}
      {recs?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {recs.map((r, i) => {
            const Icon = ICON[r.engine] || AlertTriangle;
            const to = ROUTE[r.engine] || "/inventory";
            const clickable = canAccess(to);
            const tone = TONE[r.engine] || "info";
            return (
              <div key={r.id} data-testid={`top-decision-${i + 1}`}
                   onClick={() => clickable && navigate(to)}
                   style={{ animationDelay: `${i * 60}ms` }}
                   className={cn("p-3 border border-border rounded-sm bg-surface-2/40 transition-colors group animate-fade-in",
                     clickable && "hover:bg-surface-2 hover:border-border-strong cursor-pointer")}>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className={cn("w-7 h-7 rounded-sm flex items-center justify-center shrink-0",
                    tone === "critical" ? "bg-critical/15 text-critical" : tone === "warning" ? "bg-warning/15 text-warning" :
                    tone === "success" ? "bg-success/15 text-success" : "bg-info/15 text-info")}>
                    <Icon size={13} />
                  </div>
                  <OriginBadge {...recOrigin(r)} testid={`top-decision-${i + 1}-origin`} />
                </div>
                <div className="text-[9px] uppercase tracking-widest text-muted font-semibold truncate mb-1">{r.engine_label}</div>
                <div className="text-[13px] font-medium leading-snug line-clamp-2 mb-1">{r.title}</div>
                <div className="text-[10px] text-muted leading-snug line-clamp-2 mb-2">
                  {[r.scope?.sku_name, r.scope?.branch_name].filter(Boolean).join(" · ") || "Network"}
                </div>
                <div className="flex items-center justify-between border-t border-border pt-2">
                  <span className="font-mono-tabular text-sm font-semibold text-primary">{formatPeso(r.expected_value_php || 0, { compact: true })}</span>
                  {clickable && <span className="text-[10px] text-primary opacity-70 group-hover:opacity-100">→</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Tile>
  );
}
